import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Avatar,
  Box,
  Chip,
  Typography,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Cancel,
  HourglassEmpty
} from '@mui/icons-material';

interface UserPaymentData {
  id: string;
  email: string;
  displayName: string;
  hasSubscription: boolean;
  subscriptionStatus?: string;
  subscriptionStart?: Date;
  subscriptionEnd?: Date;
  amountPaid?: number;
  lastPaymentDate?: Date;
  paidTrips: number;
}

interface UserPaymentStatusProps {
  users: UserPaymentData[];
}

type SortOrder = 'asc' | 'desc';
type SortField = 'displayName' | 'email' | 'hasSubscription' | 'subscriptionStatus' | 'subscriptionStart' | 'subscriptionEnd' | 'amountPaid';

const UserPaymentStatus: React.FC<UserPaymentStatusProps> = ({ users }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState<SortField>('email');
  const [searchTerm, setSearchTerm] = useState('');

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sort handler
  const handleRequestSort = (property: SortField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Create sort handler for a specific field
  const createSortHandler = (property: SortField) => () => {
    handleRequestSort(property);
  };

  // Format date
  const formatDate = (date: Date | undefined) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined) return '—';
    return `$${amount.toFixed(2)}`;
  };

  // Get subscription status chip
  const getSubscriptionStatusChip = (user: UserPaymentData) => {
    if (!user.hasSubscription) {
      return (
        <Chip 
          icon={<Cancel fontSize="small" />}
          label="None"
          size="small"
          color="default"
          variant="outlined"
        />
      );
    }
    
    const status = user.subscriptionStatus?.toLowerCase() || '';
    
    if (status === 'active') {
      return (
        <Chip 
          icon={<CheckCircle fontSize="small" />}
          label="Active"
          size="small"
          color="success"
        />
      );
    } else if (status === 'canceled' || status === 'cancelled') {
      return (
        <Chip 
          icon={<Cancel fontSize="small" />}
          label="Canceled"
          size="small"
          color="error"
        />
      );
    } else if (status === 'trialing') {
      return (
        <Chip 
          icon={<HourglassEmpty fontSize="small" />}
          label="Trial"
          size="small"
          color="warning"
        />
      );
    }
    
    return (
      <Chip 
        label={status || 'Unknown'}
        size="small"
        color="default"
      />
    );
  };

  // Filter and sort the users data
  const filteredAndSortedUsers = React.useMemo(() => {
    // First filter by search term
    const filtered = users.filter(user => {
      if (!searchTerm) return true;
      
      const searchLower = searchTerm.toLowerCase();
      return (
        user.displayName.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        (user.subscriptionStatus && user.subscriptionStatus.toLowerCase().includes(searchLower))
      );
    });
    
    // Then sort by the selected field
    return filtered.sort((a, b) => {
      let valueA: any = a[orderBy];
      let valueB: any = b[orderBy];
      
      // Handle undefined values
      if (valueA === undefined) {
        valueA = order === 'asc' ? Infinity : -Infinity;
      }
      if (valueB === undefined) {
        valueB = order === 'asc' ? Infinity : -Infinity;
      }
      
      // For date objects, convert to timestamps
      if (valueA instanceof Date && valueB instanceof Date) {
        valueA = valueA.getTime();
        valueB = valueB.getTime();
      }
      
      // For booleans
      if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
        valueA = valueA ? 1 : 0;
        valueB = valueB ? 1 : 0;
      }
      
      // Compare values
      if (order === 'asc') {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
      }
    });
  }, [users, searchTerm, order, orderBy]);

  // Calculate pagination
  const paginatedUsers = filteredAndSortedUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate stats
  const totalUsers = users.length;
  const usersWithSubscription = users.filter(user => user.hasSubscription).length;
  const usersWithPayments = users.filter(user => (user.amountPaid || 0) > 0).length;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ bgcolor: 'background.paper', p: 1, borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="body2" color="text.secondary">Total Users</Typography>
            <Typography variant="h6">{totalUsers}</Typography>
          </Box>
          
          <Box sx={{ bgcolor: 'background.paper', p: 1, borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="body2" color="text.secondary">With Subscription</Typography>
            <Typography variant="h6">{usersWithSubscription}</Typography>
          </Box>
          
          <Box sx={{ bgcolor: 'background.paper', p: 1, borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="body2" color="text.secondary">With Payments</Typography>
            <Typography variant="h6">{usersWithPayments}</Typography>
          </Box>
        </Box>
        
        <TextField
          placeholder="Search users..."
          size="small"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: '250px' } }}
        />
      </Box>
      
      <TableContainer component={Paper}>
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'displayName'}
                  direction={orderBy === 'displayName' ? order : 'asc'}
                  onClick={createSortHandler('displayName')}
                >
                  User
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'email'}
                  direction={orderBy === 'email' ? order : 'asc'}
                  onClick={createSortHandler('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'hasSubscription'}
                  direction={orderBy === 'hasSubscription' ? order : 'asc'}
                  onClick={createSortHandler('hasSubscription')}
                >
                  Subscription
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'subscriptionStart'}
                  direction={orderBy === 'subscriptionStart' ? order : 'asc'}
                  onClick={createSortHandler('subscriptionStart')}
                >
                  Start Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'subscriptionEnd'}
                  direction={orderBy === 'subscriptionEnd' ? order : 'asc'}
                  onClick={createSortHandler('subscriptionEnd')}
                >
                  Renewal Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'amountPaid'}
                  direction={orderBy === 'amountPaid' ? order : 'asc'}
                  onClick={createSortHandler('amountPaid')}
                >
                  Trip Payments
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          
          <TableBody>
            {paginatedUsers.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ width: 32, height: 32, mr: 1.5 }}>
                      {user.displayName.charAt(0)}
                    </Avatar>
                    <Typography variant="body2">
                      {user.displayName || 'Unknown User'}
                    </Typography>
                  </Box>
                </TableCell>
                
                <TableCell>{user.email}</TableCell>
                
                <TableCell>{getSubscriptionStatusChip(user)}</TableCell>
                
                <TableCell>{formatDate(user.subscriptionStart)}</TableCell>
                
                <TableCell>{formatDate(user.subscriptionEnd)}</TableCell>
                
                <TableCell>
                  <Typography 
                    fontWeight={500} 
                    color={user.amountPaid && user.amountPaid > 0 ? 'success.main' : 'text.secondary'}
                  >
                    {formatCurrency(user.amountPaid)}
                  </Typography>
                  {user.paidTrips > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {user.paidTrips} {user.paidTrips === 1 ? 'trip' : 'trips'}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            
            {paginatedUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm 
                      ? 'No users found matching your search'
                      : 'No users found'
                    }
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          component="div"
          count={filteredAndSortedUsers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>
    </Box>
  );
};

export default UserPaymentStatus; 